// Generic ERP sync run over Core's /api/sync routes (spec §5.9). A tenant pack
// that names `sync.adapter` runs through here; packs that still declare a
// legacy `sync.endpoint` POST keep their own call site until Phase 4.
import { authFetch } from '@/utils/authFetch';

export interface SyncRun { runId: string; status: 'running' | 'ok' | 'failed'; summary?: Record<string, unknown> | null; error?: string | null; [k: string]: unknown }
const base = () => import.meta.env.VITE_API_BASE_URL || '';
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function readError(res: Response, fallback: string): Promise<string> { const b = await res.json().catch(() => ({})); return (b as { error?: string; details?: string }).details || (b as { error?: string }).error || fallback; }

/** POST /api/sync/:adapter/run then poll the run record until it is ok or failed (spec §5.9). */
export async function runSyncAndWait(adapter: string, opts: { optimizerStartDate?: string; optimizeAll?: boolean; fetchImpl?: typeof authFetch; pollMs?: number; timeoutMs?: number } = {}): Promise<SyncRun> {
  const f = opts.fetchImpl ?? authFetch; const pollMs = opts.pollMs ?? 2000; const deadline = Date.now() + (opts.timeoutMs ?? 30 * 60_000);
  // `optimizeAll` reaches the adapter as ctx.optimizeAll (Core threads it through SyncRunner) — an adapter
  // that writes the jobs dataset passes it on so the checkbox clears the locks the same way an upload does.
  const body: { optimizerStartDate?: string; optimizeAll?: boolean } = {};
  if (opts.optimizerStartDate) body.optimizerStartDate = opts.optimizerStartDate;
  if (opts.optimizeAll !== undefined) body.optimizeAll = opts.optimizeAll;
  const started = await f(`${base()}/api/sync/${encodeURIComponent(adapter)}/run`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!started.ok) throw new Error(await readError(started, `HTTP ${started.status}`));
  const { runId } = (await started.json()) as { runId: string };
  for (;;) {
    const res = await f(`${base()}/api/sync/${encodeURIComponent(adapter)}/runs/${encodeURIComponent(runId)}`);
    if (!res.ok) throw new Error(await readError(res, `HTTP ${res.status}`));
    const run = (await res.json()) as SyncRun;
    if (run.status === 'ok') return run;
    if (run.status === 'failed') throw new Error(run.error || 'sync failed');
    if (Date.now() > deadline) throw new Error('sync timed out');
    await sleep(pollMs);
  }
}
